import { memo, Suspense, useCallback, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAlert } from "../../contexts/alertContext";

import MuiSnackbar from "components/UI/MuiSnackbar";

import Listing from "Pages/Listing";
import { Button, Stack } from "@mui/material";
import ActionLoader from "components/UI/ActionLoader";
import Header from "components/Header";
import { LocalStorage } from "enums/localStorage";
import useLoading from "hooks/loading";
import useApp from "hooks/useApp";
import useAuthStore from "store/auth";
import { useTranslation } from "react-i18next";
import SideMenu from "components/SideMenu";
import { getUserData } from "helpers";
import { UserRoleName } from "types/auth";
import { ELanguage } from "models/app";
import EmployeeWorkshopAssessmentResult from "Pages/EmployeeWorkshopAssessment/EmployeeWorkshopAssessmentResult";

const Dashboard: React.FC = () => {
  const { alert } = useAlert();
  const { i18n } = useTranslation();
  const setSession = useAuthStore((state) => state.setSession);
  const { pathname, search } = useLocation();
  const { push } = useApp();
  const { loading, setLoading } = useLoading();

  const userData = getUserData();
  const { roleName, user, isApprovedVendor } = userData;

  const redirectToLogin = (): void => {
    setSession(null);
    setLoading(false);
    localStorage.removeItem(LocalStorage.ACCESS_TOKEN);
    localStorage.removeItem(LocalStorage.REFRESH_TOKEN);

    push("/login");
  };

  const getRedirectPath = (roleName: UserRoleName): string => {
    switch (roleName) {
      case UserRoleName.EMPLOYEE:
      case UserRoleName.HEAD_OF_DEPARTMENT:
      case UserRoleName.COMPANY_ADMIN:
      case UserRoleName.CORPORATE_ADMIN:
      case UserRoleName.SUPERADMIN:
        return "/workshops";

      default:
        return "/dashboard";
    }
  };

  const initApp = useCallback(async () => {
    try {
      setSession(JSON.parse(localStorage.getItem(LocalStorage.PROFILE)!));

      setLoading(false);

      push(search.length === 0 ? pathname : pathname + search);
    } catch (err: Error | any) {
      redirectToLogin();
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const accessToken: string | null = localStorage.getItem(
      LocalStorage.ACCESS_TOKEN
    );
    const user = JSON.parse(localStorage.getItem(LocalStorage.USER!)!)
    if (user && user?.languageCode) {
      i18n.changeLanguage(
        user.languageCode === 1 ? ELanguage.ARABIC : ELanguage.ENGLISH
      );
    }

    if (accessToken) initApp();
    else redirectToLogin();

    // eslint-disable-next-line
  }, []);

  const handleLogout = () => {

    localStorage.clear();
    push("/login");
    return;
  };

  if (loading) return <ActionLoader position="fixed" />;

  if (roleName === UserRoleName.VENDOR_ADMIN && !isApprovedVendor) return <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', position: 'relative' }}>
    <div>Your registration is not yet approved</div>
    <div style={{ position: 'absolute', right: '20px', top: '20px' }}>
      <Button onClick={handleLogout} style={{ fontSize: '20px' }} variant="contained">Logout</Button>
    </div>

  </div>
  return (
    <Stack
      component="main"
      sx={{ direction: i18n.language === "en" ? "ltr" : "rtl" }}
      direction={"row"}
      bgcolor="background.default"
      width={"100dvw"}
      height={"100dvh"}
      useFlexGap
    >
      <Suspense fallback={<ActionLoader position="fixed" />}>
        {pathname !== '/register' && <SideMenu />}
        <Stack
          width={1}
          height={1}
          p={2}
          minHeight={"100vh"}
          flexGrow={1}
          sx={{ overflowY: "auto" }}
        >
          <Header />

          <Suspense fallback={<ActionLoader position="fixed" />}>
            <Routes>
              <Route path="employeeWorkshopAssessment/result/:assessmentDefinitionId" element={<EmployeeWorkshopAssessmentResult />} />
              <Route path="/*" element={<Navigate to={getRedirectPath(roleName as UserRoleName)} />} />
              <Route path=":slug/*" element={<Listing />} />
            </Routes>
          </Suspense>
          <div
            style={{
              bottom: 0,
              width: "100%",
              display: "flex",
              padding: "3px 0",
              marginTop: "auto",
              justifyContent: "center",
              alignItems: "center",
              background: "rgba(227, 222, 220,0.7)",
            }}
          >
            Developed by Emerging Systems
          </div>
        </Stack>
      </Suspense>
      <MuiSnackbar alert={alert!} />
    </Stack>
  );
};

export default memo(Dashboard);
